import {observable, action, computed} from 'mobx';
import {DomainStore} from 'shared/store';
import {endpoints, types, t} from 'shared/core';
import _ from 'lodash';
import {PerformanceReviewReview, PerformanceReviewQuestion} from 'stores/performance_reviews';
import {setupAutosaveDraft, successAlert} from 'shared/tools';

class WriteReviewState {
  store = new DomainStore();
  history;
  match;

  @observable submitReviewModalOpen = false;
  @observable currentReview;
  @observable reviews = [];
  @observable isLoadingReview = false;
  @observable errors = {};

  async receiveProps({history, match}) {
    const oldMatch = this.match;
    this.history = history;
    this.match = match;

    if (!match.params.reviewId) {
      return await this.load();
    }

    if (_.get(oldMatch, 'params.reviewId') !== this.match.params.reviewId) {
      await this.loadRevieweeDetails(this.match.params.reviewId);
    }
  }

  @action async load() {
    const {cycleId, reviewId} = this.match.params;

    await this.store._compose([
      endpoints.PERFORMANCE_REVIEWS.REVIEWS.with(cycleId)
    ]);

    this.reviews = this.store._getAll(
      types.PERFORMANCE_REVIEWS.REVIEW, PerformanceReviewReview
    );

    if (reviewId) {
      await this.loadRevieweeDetails(reviewId);
    } else {
      await this.setCurrentReview();
    }
  }

  @action async loadRevieweeDetails(reviewId) {
    if (this.isLoadingReview) return;

    this.store.invalidate(types.PERFORMANCE_REVIEWS.QUESTION);
    this.isLoadingReview = true;
    this.errors = {};

    const {cycleId} = this.match.params;

    await this.store._compose([
      endpoints.PERFORMANCE_REVIEWS.REVIEW.with(cycleId, reviewId)
    ]);

    this.currentReview = new PerformanceReviewReview(
      this.store._getSingle(types.PERFORMANCE_REVIEWS.REVIEW, { id: reviewId })
    );

    const questions = this.store._getAll(
      types.PERFORMANCE_REVIEWS.QUESTION, PerformanceReviewQuestion
    );

    if (!this.currentReview.readOnly) {
      await setupAutosaveDraft(
        this.currentReview,
        {id: this.currentReview.id, type: types.PERFORMANCE_REVIEWS.REVIEW}
      );
      this.currentReview.regenerateAnswersForQuestions(questions);
    }

    this.isLoadingReview = false;
  }

  @action async setCurrentReview() {
    let incompleteReview = _.find(this.selfReview, { status: 'pending' });

    if (!incompleteReview) {
      incompleteReview = _.find(this.directReportReviews, { status: 'pending' });
    }

    if (!incompleteReview) {
      incompleteReview = _.head(this.reviews);
    }

    this.updateBrowserPath(incompleteReview.id);
    await this.loadRevieweeDetails(incompleteReview.id);
  }

  @action onAnswerChanged() {
    this.currentReview.autosaver.autosave();
  }

  @action updateBrowserPath(reviewId) {
    this.history.push(`/reviews/${this.match.params.cycleId}/write/${reviewId}`);
  }

  @action async submitReview() {
    const {model, errors} = await this.store.patch(
      this.currentReview.link('submit'),
      types.PERFORMANCE_REVIEWS.REVIEW,
      this.currentReview
    );
    this.errors = errors;

    if (model) {
      await this.load();
      this.currentReview.update(model);
      successAlert(t('performance_reviews.view.alerts.Review has been submitted!'));
    }

    this.closeSubmitReviewModal();
  }

  @action openSubmitReviewModal() {
    this.submitReviewModalOpen = true;
  }

  @action closeSubmitReviewModal() {
    this.submitReviewModalOpen = false;
  }

  @action async unsubmitReview() {
    const {model, errors} = await this.store.patch(
      this.currentReview.link('unsubmit'),
      types.PERFORMANCE_REVIEWS.REVIEW,
      this.currentReview
    );
    this.errors = errors;

    if (model) {
      await this.load();
      this.currentReview.merge(model);
    }
  }

  @computed get directReportReviews() {
    return _.filter(this.reviews, { type: { kind: 'manager' }});
  }

  @computed get hasDirectReportReviews() {
    return !_.isEmpty(this.directReportReviews);
  }

  @computed get selfReview() {
    return _.filter(this.reviews, { type: { kind: 'self' }});
  }

  @computed get hasSelfReview() {
    return !_.isEmpty(this.selfReview);
  }

  @computed get peerReviews() {
    return _.filter(this.reviews, { type: { kind: 'peer' }});
  }

  @computed get hasPeerReviews() {
    return !_.isEmpty(this.peerReviews);
  }

  @computed get visibilityNotice() {
    const kind = _.get(this.currentReview, 'type.kind');
    switch(kind) {
      case 'peer':
        return this.currentReview.reviewee.canViewAllReviews ?
          'performance_reviews.view.PEER_VISIBILITY_NOTICE_NO_WARNING' :
          'performance_reviews.view.PEER_VISIBILITY_NOTICE';
      case 'direct_report':
        return 'performance_reviews.view.DIRECT_REPORT_VISIBILITY_NOTICE';
      case 'manager':
        return 'performance_reviews.view.MANAGER_VISIBILITY_NOTICE';
      default:
        return 'performance_reviews.view.VISIBILITY_NOTICE';
    }
  }
}

export default WriteReviewState;
